import type { WorksheetSpecV1 } from './spec'
import type { WorksheetQueryExecution } from './worksheetQueryArtifacts'
import type { WorksheetQueryOptions } from './worksheetQueryBuilder'
import { assertFailFastInvariant } from './worksheetFailFast'
import {
  lowerWorksheetAggregateBase,
  lowerWorksheetCompilationToExecution,
  lowerWorksheetRelationBinding,
} from './worksheetQueryLowering'

/**
 * Build a worksheet query, optionally emitting a deterministic DuckDB execution plan.
 */
export function buildWorksheetQueryExecution(
  spec: WorksheetSpecV1,
  options: WorksheetQueryOptions & { emitExecPlan?: boolean; collectExecPlan?: boolean }
): WorksheetQueryExecution {
  const queryOptions = {
    ...(options as any),
    emitExecPlan: Boolean((options as any)?.emitExecPlan),
    collectExecPlan: Boolean((options as any)?.collectExecPlan ?? (options as any)?.collect_exec_plan),
  }
  const relationBinding = lowerWorksheetRelationBinding(spec, queryOptions)
  const compilation = lowerWorksheetAggregateBase(relationBinding)
  return lowerWorksheetCompilationToExecution(compilation, queryOptions)
}

const stableStringify = (value: any): string => {
  const seen = new WeakSet<object>()
  const normalize = (current: any): any => {
    if (current === null || typeof current === 'undefined') return current
    if (typeof current === 'bigint') return current.toString()
    if (typeof current !== 'object') return current
    if (seen.has(current as object)) return '[Circular]'
    seen.add(current as object)
    if (Array.isArray(current)) return current.map(normalize)
    const out: Record<string, any> = {}
    for (const key of Object.keys(current).sort()) out[key] = normalize(current[key])
    return out
  }
  try {
    return JSON.stringify(normalize(value))
  } catch {
    return String(value ?? '')
  }
}

export const summarizeWorksheetQueryExecutionForDrift = (
  execution: WorksheetQueryExecution,
): Record<string, any> => {
  const plan = execution.kind === 'plan' ? execution.plan : null
  return {
    kind: execution.kind,
    sql: String(execution.sql ?? '').trim(),
    plan: plan
      ? {
          statements: Array.isArray(plan.statements)
            ? plan.statements.map((stmt) => String(stmt ?? '').trim())
            : [],
          finalSql: String(plan.finalSql ?? '').trim(),
          cleanup: Array.isArray(plan.cleanup)
            ? plan.cleanup.map((stmt) => String(stmt ?? '').trim())
            : [],
          materializedTable: String(plan.materializedTable ?? '').trim() || null,
          materializedSql: String(plan.materializedSql ?? '').trim() || null,
          aggBaseMaterializedTable: String(plan.aggBaseMaterializedTable ?? '').trim() || null,
          aggBaseMaterializedSql: String(plan.aggBaseMaterializedSql ?? '').trim() || null,
          aggBaseWrapperSql: String(plan.aggBaseWrapperSql ?? '').trim() || null,
          aggBaseWrapperSourceTable: String(plan.aggBaseWrapperSourceTable ?? '').trim() || null,
        }
      : null,
    compilation: {
      finalSql: String(execution.compilation?.finalSql ?? '').trim(),
      aggBaseSql: String(execution.compilation?.aggBaseSql ?? '').trim() || null,
      signatures: {
        finalSqlHash: String(execution.compilation?.signatures?.finalSqlHash ?? '').trim() || null,
        aggBaseSqlHash: String(execution.compilation?.signatures?.aggBaseSqlHash ?? '').trim() || null,
        aggBaseSignature: String(execution.compilation?.signatures?.aggBaseSignature ?? '').trim() || null,
      },
      diagnostics: {
        execPlanRequested: Boolean(execution.compilation?.diagnostics?.execPlanRequested),
        execPlanGenerated: Boolean(execution.compilation?.diagnostics?.execPlanGenerated),
        densifyEnabled: Boolean(execution.compilation?.diagnostics?.densifyEnabled),
        includeTotalsEnabled: Boolean(execution.compilation?.diagnostics?.includeTotalsEnabled),
      },
    },
  }
}

export const assertNoWorksheetQueryExecutionDrift = (input: {
  expected: WorksheetQueryExecution
  actual: WorksheetQueryExecution
  context?: Record<string, any> | null
}): void => {
  const expectedSummary = summarizeWorksheetQueryExecutionForDrift(input.expected)
  const actualSummary = summarizeWorksheetQueryExecutionForDrift(input.actual)
  assertFailFastInvariant(
    stableStringify(expectedSummary) === stableStringify(actualSummary),
    {
      name: 'worksheet_query_execution_drift',
      details: {
        ...(input.context || {}),
        expected: expectedSummary,
        actual: actualSummary,
      },
    },
  )
}
